/**
 * Centralized session-expired handling for 401/403 across all API clients.
 * Shows a localized Modal warning, then redirects to signin (flow unchanged).
 */

import React from "react";
import { message, Modal } from "antd";
import { removeItems, removeItemsBySession } from "util/localStorage";
import { stateSet } from "constants/StateDefault";
import { userSignOutSuccess } from "appRedux/actions/Auth";
import { notifySessionExpired } from "util/authChannel";

let sessionExpiredHandled = false;

const SESSION_EXPIRED_BODY_CLASS = "session-expired-active";

type SessionExpiredCopy = {
  title: string;
  content: string;
  okText: string;
  redirectHint: string;
  categories: {
    token: string;
    device: string;
    timeout: string;
    generic: string;
  };
};

/** Session Expired modal copy: formal English, Thai, Chinese */
const SESSION_EXPIRED_MESSAGES: Record<string, SessionExpiredCopy> = {
  en: {
    title: "Session Expired",
    content:
      "Your session has ended. This may happen when you sign in on another device or tab, or when your session has timed out.\n\nYou will be redirected to the sign-in page. Please sign in again to continue.",
    okText: "OK",
    redirectHint: "You will be redirected to the sign-in page. Please sign in again to continue.",
    categories: {
      token: "Authentication problem",
      device: "Signed in on another device",
      timeout: "Session timeout",
      generic: "Session error",
    },
  },
  th: {
    title: "เซสชันหมดอายุ",
    content:
      "เซสชันการใช้งานของท่านได้สิ้นสุดลง อาจเกิดจากการลงชื่อเข้าใช้บนอุปกรณ์หรือแท็บอื่น หรือเซสชันหมดเวลา\n\nระบบจะนำท่านกลับไปหน้าลงชื่อเข้าใช้ กรุณาลงชื่อเข้าใช้ใหม่อีกครั้งเพื่อดำเนินการต่อ",
    okText: "ตกลง",
    redirectHint: "ระบบจะนำท่านกลับไปหน้าลงชื่อเข้าใช้ กรุณาลงชื่อเข้าใช้ใหม่อีกครั้งเพื่อดำเนินการต่อ",
    categories: {
      token: "ปัญหาการยืนยันตัวตน",
      device: "มีการเข้าสู่ระบบจากอุปกรณ์อื่น",
      timeout: "เซสชันหมดเวลา",
      generic: "ข้อผิดพลาดของเซสชัน",
    },
  },
  zh: {
    title: "会话已过期",
    content:
      "您的会话已结束。这可能是因为您在另一台设备或标签页上登录，或会话已超时。\n\n即将跳转至登录页，请重新登录以继续使用。",
    okText: "确定",
    redirectHint: "即将跳转至登录页，请重新登录以继续使用。",
    categories: {
      token: "身份验证问题",
      device: "已在其他设备登录",
      timeout: "会话超时",
      generic: "会话错误",
    },
  },
};

/** Max length of the backend reason shown to the user (defensive cap). */
const REASON_MAX_LENGTH = 300;

function normalizeSessionExpiredReason(raw: unknown): string {
  if (raw === null || raw === undefined) return "";
  const text = String(raw).trim();
  if (!text) return "";
  return text.length > REASON_MAX_LENGTH ? `${text.slice(0, REASON_MAX_LENGTH)}…` : text;
}

/** Bucket the backend message into a human category for the box header. */
function categorizeSessionExpiredReason(reason: string, copy: SessionExpiredCopy): string {
  const lower = reason.toLowerCase();
  if (lower.includes("another") || lower.includes("device") || lower.includes("logged out")) {
    return copy.categories.device;
  }
  if (lower.includes("expired") || lower.includes("timeout") || lower.includes("timed out")) {
    return copy.categories.timeout;
  }
  if (lower.includes("token") || lower.includes("signature") || lower.includes("invalid")) {
    return copy.categories.token;
  }
  return copy.categories.generic;
}

/**
 * Categorized error box: category pill → backend message → redirect hint.
 * Shown INSTEAD of the default paragraph when the API provides a message.
 */
function renderSessionExpiredReason(reason: string, copy: SessionExpiredCopy): React.ReactNode {
  return (
    <div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginBottom: 8,
          padding: "2px 10px",
          borderRadius: 999,
          background: "#fff1f0",
          border: "1px solid #ffccc7",
          color: "#cf1322",
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {categorizeSessionExpiredReason(reason, copy)}
      </div>
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          background: "#fafafa",
          border: "1px solid #f0f0f0",
          color: "#434343",
          fontSize: 13,
          lineHeight: 1.6,
          wordBreak: "break-word",
        }}
      >
        {reason}
      </div>
      <div style={{ marginTop: 12, color: "#8c8c8c", fontSize: 12.5, lineHeight: 1.5 }}>
        {copy.redirectHint}
      </div>
    </div>
  );
}

const DEFAULT_MESSAGES = SESSION_EXPIRED_MESSAGES.en;

function getCurrentLocale(): string {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem("locale");
    if (!stored) return "en";
    const obj = JSON.parse(stored);
    const locale = obj && obj.locale ? String(obj.locale).toLowerCase() : "en";
    return locale in SESSION_EXPIRED_MESSAGES ? locale : "en";
  } catch {
    return "en";
  }
}

/**
 * Clear auth-related storage (mirrors Auth saga signOut cleanup).
 */
export function clearAuthStorage(): void {
  try {
    removeItems(["access_token", stateSet]);
    removeItemsBySession(["report_active_menu_category"]);
    if (typeof localStorage !== "undefined") localStorage.clear();
    if (typeof sessionStorage !== "undefined") sessionStorage.clear();
  } catch (e) {
    // no-op
  }
}

/**
 * Handle session expired / invalid token: show localized Modal warning, then clear storage and redirect.
 */
export function handleSessionExpired(
  store: { dispatch: (action: any) => void },
  reasonMessage?: string
): void {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;

  const locale = getCurrentLocale();
  const messages = SESSION_EXPIRED_MESSAGES[locale] || DEFAULT_MESSAGES;
  // Backend message (e.g. "Invalid token: token signature is invalid") is the
  // real cause — when present, show ONLY it (categorized box); else default copy.
  const reason = normalizeSessionExpiredReason(reasonMessage);

  // Session expiry is already communicated by the blocking modal. Remove any
  // pending toast and keep late 401/403 handlers from displaying another one.
  message.destroy();
  if (typeof document !== "undefined") {
    document.body.classList.add(SESSION_EXPIRED_BODY_CLASS);
  }

  notifySessionExpired(messages.title);

  Modal.warning({
    title: messages.title,
    content: reason ? renderSessionExpiredReason(reason, messages) : messages.content,
    okText: messages.okText,
    maskClosable: false,
    width: 440,
    styles: { body: { whiteSpace: "pre-line" } },
    onOk: () => {
      message.destroy();
      sessionExpiredHandled = false;
      clearAuthStorage();
      store.dispatch(userSignOutSuccess());
      if (typeof window !== "undefined") {
        window.location.replace("/signin");
      }
    },
  });
}

/**
 * Check API response for 401 or 403 that should trigger session expired.
 * Returns true if caller should treat as session expired (handler was or will be called).
 */
export function isSessionInvalidResponse(
  status: number,
  data?: { status?: number; data?: any }
): boolean {
  if (status === 401) {
    // Only treat as session expired if the user has an active token.
    // If no token exists, this is a genuine auth error (e.g. wrong credentials on login page).
    if (typeof localStorage !== 'undefined' && !localStorage.getItem('access_token')) {
      return false;
    }
    // If the API returns a specific business-logic message (not related to token/session),
    // let the caller handle it instead of showing the session-expired modal.
    const msg = String((data as any)?.message ?? '').toLowerCase();
    if (msg && !msg.includes('token') && !msg.includes('unauthorized') && !msg.includes('expired') && !msg.includes('session')) {
      return false;
    }
    return true;
  }
  if (status === 403 || data?.status === 403) {
    if (data?.data !== undefined && data?.data !== null) return true;
    const msg = String((data as any)?.message ?? '');
    const lower = msg.toLowerCase();
    if (lower.includes('token') || lower.includes('invalid') || lower.includes('access') || lower.includes('forbidden') || lower.includes('logged out')) return true;
  }
  return false;
}
